import {produce} from 'immer';
import React, {useMemo} from 'react';

import {getTitleForView, isTopLevelAnalysisView} from 'analysis-tool/data-view/AnalysisViewUtils';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  PipelineGraphQL,
  pipelineStepIsStepType,
  PipelineStep,
  PipelineViewGraphQL,
} from 'pipelines/PipelineTypes';
import {
  getChildViewsForView,
  getDefaultClientIdForView,
  getOutputDataForView,
  isChartView,
  isDataFrameView,
  isFileView,
  resolveLineageStep,
} from 'pipelines/PipelineViewUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {assertNever} from 'util/Types';

/**
 * Transform views that end with CHART_VIEW steps to return the step's "Output dataset" rather than "Output chart".
 *
 * This function no-ops on views that do not end with CHART_VIEW steps.
 */
export function mapChartViewsToDataFrameViews(
  views: Array<PipelineViewGraphQL>,
  steps: ReadonlyArray<PipelineStep>
): Array<PipelineViewGraphQL> {
  return views.map((view) => {
    const lastNode = view.lineage[view.lineage.length - 1];
    if (lastNode == null) {
      return view;
    }
    const lastStep = resolveLineageStep(steps, lastNode);
    if (lastStep.stepType !== PipelineStepType.CHART_VIEW) {
      return view;
    }
    const outputDataset = lastStep.outputStepDatas.find((stepData) => stepData.name === 'Output dataset')!;
    return produce(view, (draftView) => {
      const draftLastNode = draftView.lineage[draftView.lineage.length - 1];
      draftLastNode.stepOutputReference = {
        // Can safely assert that stepOutputReference and output datas exist because caller has
        // already checked that the step has output data
        ...draftLastNode.stepOutputReference!,
        outputStepData: outputDataset,
        rawDataIndex: 0,
      };
    });
  });
}

function matchesViewKind(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>,
  dataKind?: PipelineStepDataKind
): boolean {
  if (getOutputDataForView(view, steps) == null) {
    return false;
  }

  switch (dataKind) {
    case undefined:
      return true;
    case PipelineStepDataKind.DATASET: {
      if (isDataFrameView(view, steps)) {
        return true;
      }
      const lastNode = view.lineage[view.lineage.length - 1];
      return (
        lastNode != null &&
        pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, resolveLineageStep(steps, lastNode))
      );
    }
    case PipelineStepDataKind.CHART:
      return isChartView(view, steps);
    case PipelineStepDataKind.FILE:
      return isFileView(view, steps);
    default:
      assertNever(dataKind);
      return false;
  }
}

function getAllDescendantViews(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  visited: Set<string> = new Set(),
  dataKind?: PipelineStepDataKind,
  depth: number = 1
): Array<{
  view: PipelineViewGraphQL;
  depth: number;
}> {
  const viewId = getDefaultClientIdForView(view, analysis.steps);
  if (visited.has(viewId)) {
    return [];
  }
  visited.add(viewId);

  const allDescendants = [];
  for (const childView of dataKind === PipelineStepDataKind.DATASET
    ? mapChartViewsToDataFrameViews(getChildViewsForView(analysis, view), analysis.steps)
    : getChildViewsForView(analysis, view)) {
    if (
      !matchesViewKind(childView, analysis.steps, dataKind) ||
      isTopLevelAnalysisView(childView, analysis)
    ) {
      continue;
    }
    allDescendants.push({view: childView, depth});
    allDescendants.push(...getAllDescendantViews(analysis, childView, visited, dataKind, depth + 1));
  }
  return allDescendants;
}

function getViewsForSelector(
  analysis: PipelineGraphQL,
  topLevelOnly: boolean,
  dataKind?: PipelineStepDataKind
): Array<{
  topLevelView: PipelineViewGraphQL;
  childViews: Array<{
    view: PipelineViewGraphQL;
    depth: number;
  }>;
}> {
  const topLevelViews = analysis.views.filter(
    (view) => isTopLevelAnalysisView(view, analysis) && matchesViewKind(view, analysis.steps, dataKind)
  );
  // Reuse visitedViewIds so that views with multiple parents only appear once
  const visitedViewIds = new Set<string>();

  return topLevelViews.map((rawTopLevelView) => {
    let topLevelView = rawTopLevelView;
    if (dataKind === PipelineStepDataKind.DATASET) {
      [topLevelView] = mapChartViewsToDataFrameViews([rawTopLevelView], analysis.steps);
    }
    const childViews = topLevelOnly
      ? []
      : getAllDescendantViews(analysis, rawTopLevelView, visitedViewIds, dataKind);

    return {
      topLevelView,
      childViews,
    };
  });
}

function viewToDisplayName(view: PipelineViewGraphQL | null, steps: ReadonlyArray<PipelineStep>): string {
  if (view == null) {
    return 'None';
  }
  return getTitleForView(view, steps);
}

function OptionName({
  view,
  steps,
  depth,
}: {
  view: PipelineViewGraphQL | null;
  steps: ReadonlyArray<PipelineStep>;
  depth?: number;
}): JSX.Element {
  const style = depth != null ? {paddingLeft: depth * 10} : undefined;
  return <span style={style}>{viewToDisplayName(view, steps)}</span>;
}

function getOptionData(
  view: PipelineViewGraphQL | null,
  steps: ReadonlyArray<PipelineStep>,
  depth?: number,
  disabledMessage?: string | null
): OptionData {
  return {
    name: <OptionName view={view} steps={steps} depth={depth} />,
    pureTextName: viewToDisplayName(view, steps),
    value: view,
    disabled: disabledMessage != null,
    disabledMessage: disabledMessage ?? undefined,
  };
}

export interface AnalysisViewSelectorProps {
  analysis: PipelineGraphQL;
  topLevelOnly: boolean;
  dataKind?: PipelineStepDataKind;
  selectedView: PipelineViewGraphQL | null;
  onSelect: (view: PipelineViewGraphQL | null) => void;
  getDisabledMessage?: (view: PipelineViewGraphQL) => string | null;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Selector for views in an Analysis.
 *
 * This component displays top-level views and optionally their child views.
 * Views can be filtered by their output data kind (DATASET, CHART, FILE).
 * If `DATASET` is specified, charts views will be converted to point to their output data frames and will be
 * included.
 */
export default function AnalysisViewSelector(props: AnalysisViewSelectorProps): JSX.Element {
  const viewsForSelector = useMemo(
    () => getViewsForSelector(props.analysis, props.topLevelOnly, props.dataKind),
    [props.analysis, props.topLevelOnly, props.dataKind]
  );

  const {steps} = props.analysis;
  const optionDatas: Array<OptionData> = [
    getOptionData(null, steps),
    ...viewsForSelector.flatMap(({topLevelView, childViews}) => {
      const disabledMessage =
        props.getDisabledMessage != null ? props.getDisabledMessage(topLevelView) : null;
      return [
        getOptionData(topLevelView, steps, undefined, disabledMessage),
        ...childViews.map(({view, depth}) => getOptionData(view, steps, depth, disabledMessage)),
      ];
    }),
  ];

  return (
    <SelectDropdown
      buttonSize={ButtonSize.SMALL}
      menu={
        <SearchableMenu
          height={MenuHeight.MEDIUM}
          width={MenuWidth.FULL}
          selectedValue={props.selectedView}
          onSelect={props.onSelect}
          optionDatas={optionDatas}
        />
      }
      displayName={viewToDisplayName(props.selectedView, steps)}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}
